import React, {createContext, useCallback, useContext, useState, useEffect, useReducer, useRef, ReactNode} from 'react';
import { createSocket } from '../lib/socket';
import { isEmbedded } from '../lib/embedMode';
import { ClientData, ManagedSource, ProjectUser, Source } from '../types';
import { ArticleSummary, ArticlesFeedLoadedEvent, ArticlesFeedPage } from '../types';
import { normalizeFeedRequest } from '../lib/feedRequest';
import { FeedSocketDisconnectError } from '../lib/feedDisconnectError';
import { shouldOfferSourceInFilter } from '../lib/filterSources';
import {
  addSourceScanReducer,
  completedScanUrls,
  EMPTY_SCAN_STATE,
  ScanState,
  scanningCount,
} from '../pages/addSourceScan';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

// COM-638 review round 2: opaque, monotonically-increasing correlation id
// for `fetchArticlesFeed` requests - a plain module counter, not
// `Date.now()`/`Math.random()`, since uniqueness within this module's
// lifetime is all that's needed (it's never persisted or compared across
// page loads). Module-scoped rather than per-provider-instance so it stays
// unique even if `SourcesProvider` were ever mounted more than once.
let nextArticlesFeedRequestId = 0;

function generateArticlesFeedRequestId(): string {
  nextArticlesFeedRequestId += 1;
  return `articles-feed-${nextArticlesFeedRequestId}`;
}

// COM-647 Task 3: same reasoning as `generateArticlesFeedRequestId` above,
// for `fetchManagedSources`'s own `getManagedSources`/`managedSourcesLoaded`
// round trip - a separate module counter (not shared with the feed's) so
// the two id spaces can never collide even though neither actually needs to
// avoid the other (each is only ever compared against its own event's
// payload).
let nextManagedSourcesRequestId = 0;

function generateManagedSourcesRequestId(): string {
  nextManagedSourcesRequestId += 1;
  return `managed-sources-${nextManagedSourcesRequestId}`;
}

// COM-638 fix wave (review round 3): a client-side safety net for
// `fetchArticlesFeed`. The backend's `getArticlesFeed` handler now echoes an
// `articlesFeedLoaded { error: true }` reply on its own failures (see
// backend/src/index.ts), but an access-guard rejection in
// `projectAccessCheck` happens BEFORE that handler ever runs - it emits a
// plain `error` event with no `requestId`, so it can never resolve/reject a
// specific pending entry. Without this timeout, a request that fails that
// way would leave its resolver in `articlesFeedResolversRef` forever, same
// as the original bug this whole fix wave addresses. 30s comfortably exceeds
// any real round trip; a module constant (not inlined) so the test suite and
// any future tuning have one place to change it.
const ARTICLES_FEED_TIMEOUT_MS = 30000;

// COM-640: the same client-side safety net as `ARTICLES_FEED_TIMEOUT_MS`,
// modelled on it directly (same value, kept as its own named constant since
// it guards a semantically different reply - `addSource`'s `sourceAdded`/
// `error`, not `getArticlesFeed`'s `articlesFeedLoaded`). Without it, an
// `addSource` whose `requireProjectOwner`/access-guard rejection happens
// before the handler ever runs (same gap `ARTICLES_FEED_TIMEOUT_MS` covers
// for the feed) would leave `pendingAddSourceRef` set forever - and, worse,
// leave the scanning indicator's optimistic `started` state stranded with
// only a reload able to clear it (see `addSourceScan.ts`'s doc comment).
//
// This covers only the FIRST half of the scan's window - submission through
// `sourceAdded` (the row exists). See `ADD_SOURCE_SCAN_TIMEOUT_MS` below for
// the second half.
const ADD_SOURCE_TIMEOUT_MS = 30000;

// Fix round (review, Critical 2): the safety net above used to be the ONLY
// timeout `addSource` ever armed, and it was cleared the instant
// `sourceAdded` arrived - covering just the round trip to create the row,
// not the scan itself. `backend/src/index.ts`'s `addSource` handler can
// still emit a plain in-band `error` AFTER `sourceAdded` (e.g.
// `sendAllSourcesWithFullArticles` throwing after `processContentAsync`
// already finished) without dropping the connection - with no timer armed
// for this phase and the `error` listener only ever checking the
// already-null `pendingAddSourceRef`, that error was a no-op and "Scanning 1
// source" stayed up until a manual reload.
//
// This constant re-arms the safety net for the SECOND half of the window -
// `sourceAdded` through the source's own status reaching a terminal state
// (`isOwnScanComplete`, addSourceScan.ts) - so the whole submission ->
// completion journey is covered by one continuous net, not just the first
// leg of it. Deliberately NOT reused from `ADD_SOURCE_TIMEOUT_MS`: that
// value sizes a plain socket round trip, while this phase is the entire
// scrape -> catalog-page check -> per-article relevance filter -> per-
// article summary generation pipeline (`SourcesService#processContentAsync`).
//
// Fix round 2 (COM-640 Task 2 follow-up, re-review): 120s was sized off "a
// slow-but-healthy scan", not off the pipeline's actual worst case for the
// exact scenario this feature targets - a first source add. Verified in
// `backend/src/services/sourcesService.ts#processContentAsync`: for a
// brand-new source (no saved articles yet, so the catalog-page check is
// never skipped), the sequential AI-bound stages are (1) `isArticleCatalogPage`,
// (2) one batched `filterRelevantArticles` call, then (3) one
// `generateArticleSummary` call PER newly-found article - run strictly
// sequentially, never in parallel (see AGENTS.md's logging/observability
// note on this project's deliberate no-parallelization rule), each bounded
// by `openaiService.ts`'s `AI_RESPONSE_TIMEOUT` (60_000ms). A source that
// surfaces even a handful of genuinely new articles on its first scan can
// legitimately chain several of these before finishing - nothing broken,
// just slow. Sized for a realistic worst case of 10 new articles on a first
// scan (an active regulator's catalog/RSS page plausibly listing that many
// recent items) plus the two flat catalog-check/relevance-filter stages:
//   (1 catalog check + 1 relevance filter + 10 summaries) * 60_000ms
//     = 12 * 60_000ms = 720_000ms (12 minutes)
// Still a safety NET, not a real estimate - a healthy scan settles in
// seconds to tens of seconds; this only ever fires on a genuinely stuck or
// lost scan (`sourceAdded` arrived, but nothing terminal ever will). Once it
// does fire, `timedOut` (addSourceScan.ts) stops showing the spinner
// WITHOUT abandoning the scan's own `sourceId` - see that module's doc
// comment for why: a late `sourcesLoaded` completion must still be
// recognised, not silently dropped the way the old bare `failed` transition
// dropped it.
const ADD_SOURCE_SCAN_TIMEOUT_MS = 720000;

// COM-647 Task 3: mirrors `ARTICLES_FEED_TIMEOUT_MS`'s reasoning exactly, for
// `fetchManagedSources`'s own paged/filtered/sorted `getManagedSources` round
// trip (backend/src/index.ts's handler is modelled directly on
// `getArticlesFeed`'s own `requestId` correlation - same async-handler-with-
// no-serialization race). A separate named constant (same value) rather than
// reusing `ARTICLES_FEED_TIMEOUT_MS` directly, following `ADD_SOURCE_TIMEOUT_MS`'s
// own precedent of one constant per semantically distinct wire event even
// when the value happens to match.
const MANAGED_SOURCES_TIMEOUT_MS = 30000;

// COM-647 Task 3: `updateSource`/`setSourceMonitored`/`deleteSources` are
// plain DB-only mutation round trips - no scrape/LLM pipeline runs for any
// of them (unlike `addSource`/`addSources` below), so this is sized like
// `ADD_SOURCE_TIMEOUT_MS`'s own submission-phase safety net: comfortably
// past any real round trip, there only to catch a `requireProjectOwner`
// access-guard rejection that never reaches the handler at all (same gap
// `ARTICLES_FEED_TIMEOUT_MS` documents for the feed).
const SOURCE_SETTINGS_MUTATION_TIMEOUT_MS = 30000;

// COM-639 fix wave: the `sourcesLoaded` wire payload (backend
// `getAllSourcesWithFullArticles` in dbService.ts, and the `getSources` case
// in mockSocket.ts) carries each source's full `articles` array - needed
// here to compute the Sources filter dropdown's universe (see the
// `sourcesLoaded` listener below), but not part of the shared `Source` type
// (types.ts), which every other consumer of a "list of sources" relies on
// staying article-free. Typed locally rather than widening `Source` itself.
type SourcesLoadedPayload = (Source & { articles?: unknown[] })[];

interface Project {
  id: string;
  name: string;
  role: string;
  createdAt: string;
  updatedAt: string;
  companyType?: string[];
  jurisdictions?: string[];
  topics?: string[];
  documentTypes?: string[];
  notificationFormat?: string[];
  gptModel: string;
}

interface SourcesContextType {
  loading: boolean;
  error: string | null;
  // COM-640: was fire-and-forget `void`. Now resolves on the NEXT
  // `sourceAdded` matching THIS call's own submitted url (the row was
  // created, the scan is now running) and rejects on the NEXT unattributable
  // `error` or the `ADD_SOURCE_TIMEOUT_MS` safety net if neither ever
  // arrives. There is no requestId on either event to correlate a reply to a
  // specific call (same limitation `fetchFilterSources` already documents
  // for `getSources`/`sourcesLoaded`) - `sourceAdded` carries a url so a
  // manual add's own confirmation can be told apart from source discovery's,
  // but a plain in-band `error` carries neither, so it can only ever be
  // matched to the single pre-confirmation pending call, if there is one
  // (see `pendingAddSourceRef`'s own comment for why that stays a single
  // slot even now that scans themselves are tracked as a set). Also drives
  // `scanningSourceCount`/`sourceScanCompletedCount` below via
  // `addSourceScanReducer` (pages/addSourceScan.ts) - see that module's doc
  // comment for the full started/confirmed/timedOut/finished/failed/
  // abandonAll transition story, now modelling a SET of concurrent scans
  // rather than one (whole-branch review, Critical 2 + 3).
  addSource: (name: string, url: string) => Promise<void>;
  // COM-640 (whole-branch review, Critical 2 + 3): how many of THIS client's
  // own add-source scans are currently showing the visible spinner - a
  // `watching` scan (its own completion-phase timeout already gave up, see
  // addSourceScan.ts) is excluded, exactly like the old single-scan model's
  // `isAddSourceScanning` excluded it. `0` is "idle" (ScanIndicator renders
  // the last-updated label instead); `> 0` renders "Scanning N source(s)"
  // (Figma 3142:29000) - N was always 1 before concurrent adds were
  // trackable at all.
  scanningSourceCount: number;
  // COM-640: bumped once per scan that reaches THIS client's own recognised
  // completion in a single `sourcesLoaded` reply (see `completedScanUrls`,
  // addSourceScan.ts) - never on a failure/timeout, since no scan ever ran
  // in that case, and by more than 1 at once if multiple concurrent scans
  // happen to finish in the same reply. State, not a subscription registry,
  // matching how `isAutoCompleteInProgress` already drives its own consumer
  // below. AllArticlesPage's catch-up refetch and SourcesFilter's
  // `reloadToken` both key off this value CHANGING (not its absolute
  // value), so either one increasing by any amount is a valid "something
  // finished" signal.
  sourceScanCompletedCount: number;
  // All Articles feed (COM-638 fix): restores the read/unread flip the old
  // ContentArea drove via `updateArticleStatus` - fire-and-forget, same as
  // the other emitters below. The backend's success path also emits a
  // per-source articles event (`sourceContentLoaded`-shaped) that nothing on
  // this page consumes anymore; that's fine, no listener is added for it.
  // COM-641 owns the full read/unread redesign - this is parity only.
  markArticleRead: (sourceId: string, articleUrl: string) => void;
  isConnected: boolean;
  isAutoCompleteInProgress: boolean;
  discardData: () => void;
  projects: Project[];
  setProjectData: (data: ClientData) => void;
  updateProjectData: (data: ClientData) => void;
  startSourceDiscovery: () => void;
  selectedProject: Project | null;
  selectProject: (projectId: string) => void;
  // Project users management
  projectUsers: ProjectUser[] | null;
  loadProjectUsers: () => void;
  addUserToProject: (email: string) => void;
  removeUserFromProject: (userId: string) => void;
  updateUserProjectRole: (userId: string, role: 'VIEWER' | 'OWNER') => void;
  // GPT Model
  updateProjectGPTModel: (gptModel: string) => void;
  // Permission checks
  canManageProject: () => boolean;
  // Article summary
  getArticleSummary: (url: string) => Promise<ArticleSummary | null>;
  // All Articles feed (COM-638; COM-639 added sourceIds/dateFrom/dateTo)
  fetchArticlesFeed: (params: {
    limit?: number;
    offset?: number;
    sortBy?: 'date' | 'title';
    sortDir?: 'asc' | 'desc';
    search?: string;
    sourceIds?: string[];
    dateFrom?: string;
    dateTo?: string;
  }) => Promise<ArticlesFeedPage>;
  // COM-639 fix wave (2026-08-18, Artur): the dropdown universe is NOT
  // sources whose `status` is 'completed', and NOT simply "has at least one
  // article" either (an earlier revision of this fix). The original AC read
  // "active sources only", but the feed query
  // (`DatabaseService#getArticlesFeed`) has no source-status predicate at
  // all, so an errored/in-progress source's articles still show up in the
  // unfiltered feed; a status-filtered dropdown gave those articles no
  // checkbox to be selected by, so ANY explicit selection silently excluded
  // them with no way for the user to even see they existed. Refined again by
  // Artur 2026-08-18 to a three-case rule (see `shouldOfferSourceInFilter`,
  // frontend/src/lib/filterSources.ts, for the exact predicate and its
  // rationale) that also offers a freshly added/pending source with no
  // articles yet, while still excluding a broken (errored, empty) one that
  // contributes nothing. See `fetchFilterSources` below for why this is a
  // lean lazy fetch rather than context state.
  fetchFilterSources: () => Promise<Source[]>;
  // COM-647 Task 3: the Sources Settings table's own client layer. See each
  // method's own implementation comment below for its correlation contract.
  fetchManagedSources: (params: {
    limit?: number;
    offset?: number;
    sortBy?: 'name' | 'createdAt';
    sortDir?: 'asc' | 'desc';
    search?: string;
    monitored?: boolean;
    dateFrom?: string;
    dateTo?: string;
  }) => Promise<{ sources: ManagedSource[]; total: number; offset: number }>;
  updateSource: (sourceId: string, data: { name?: string; url: string }) => Promise<ManagedSource>;
  setSourceMonitored: (sourceId: string, isMonitored: boolean) => Promise<ManagedSource>;
  deleteSources: (sourceIds: string[]) => Promise<{ deletedCount: number; sourceIds: string[] }>;
  addSources: (entries: { name?: string; url: string }[]) => Promise<ManagedSource[]>;
}

const SourcesContext = createContext<SourcesContextType | undefined>(undefined);

interface SourcesProviderProps {
  children: ReactNode;
}

export const SourcesProvider: React.FC<SourcesProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [socket, setSocket] = useState<any>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isAutoCompleteInProgress, setIsAutoCompleteInProgress] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [projectUsers, setProjectUsers] = useState<ProjectUser[] | null>(null);

  // All Articles feed (COM-638): pending `fetchArticlesFeed` resolvers,
  // keyed by the request's own opaque `requestId` (`generateArticlesFeedRequestId`
  // above).
  //
  // Review round 1 (Critical) tried matching replies by normalized offset
  // through a per-offset FIFO queue - that fixed the deterministic case (two
  // listeners both watching offset 0, the first reply firing both), but
  // review round 2 found a narrower race it couldn't express: `getArticlesFeed`
  // is an async handler with no serialization between concurrent calls on
  // one socket, so two same-offset requests (e.g. an initial load
  // immediately followed by a search reset) can complete OUT of send order
  // under real DB latency - a FIFO always hands the reply to the
  // earliest-queued resolver regardless of which request it actually
  // answers, so an out-of-order pair still cross-resolves. A per-request id
  // that both ends echo verbatim removes the ordering assumption entirely:
  // whichever reply carries a given id resolves that id's resolver, no
  // matter what order replies arrive in.
  //
  // Also cleared (rejected) on socket disconnect/reconnect below, so a
  // resolver queued right before a drop can't be silently consumed by an
  // unrelated reply after the app reconnects.
  const articlesFeedResolversRef = useRef<
    Map<string, { resolve: (data: ArticlesFeedPage) => void; reject: (error: Error) => void }>
  >(new Map());

  const rejectPendingArticlesFeedRequests = (reason: string) => {
    // `FeedSocketDisconnectError` (not a plain `Error`): this is the ONLY
    // path that should ever read as a disconnect to `isFeedDisconnectError`
    // (allArticlesFeed.ts) - see feedDisconnectError.ts for why a message
    // regex used to (wrongly) treat this as the sole rejection reason.
    articlesFeedResolversRef.current.forEach(({ reject }) => reject(new FeedSocketDisconnectError(reason)));
    articlesFeedResolversRef.current.clear();
  };

  // COM-639 fix round 1: pending `fetchFilterSources` calls, same shape of
  // problem as `articlesFeedResolversRef` above (a reply that never arrives
  // must not leak its resolver forever, and a reply crossing a
  // disconnect/reconnect must not resolve a caller who can no longer trust
  // it) but a DIFFERENT correlation problem - `getSources` carries no
  // requestId to echo back (see `backend/src/index.ts`'s handler), so
  // there is no way to match a specific reply to a specific call the way
  // `articlesFeedLoaded` is matched. A `Set`, not a `Map` keyed by id: every
  // entry waiting when ANY `sourcesLoaded` arrives gets resolved with it
  // (see the listener below for why that fan-out is the correct, not just
  // convenient, behaviour here). Not `FeedSocketDisconnectError` on
  // rejection - that marker type is specifically for `isFeedDisconnectError`
  // (allArticlesFeed.ts)'s All-Articles-feed "Connection lost" banner, a
  // different UI surface than the Sources filter dropdown this serves; a
  // plain `Error` is enough since nothing downstream needs to distinguish
  // this rejection's cause yet.
  const filterSourcesResolversRef = useRef<
    Set<{ resolve: (data: Source[]) => void; reject: (error: Error) => void }>
  >(new Set());

  const rejectPendingFilterSourcesRequests = (reason: string) => {
    filterSourcesResolversRef.current.forEach(({ reject }) => reject(new Error(reason)));
    filterSourcesResolversRef.current.clear();
  };

  // COM-647 Task 3: `fetchManagedSources`'s own pending calls, keyed by
  // `requestId` - same shape as `articlesFeedResolversRef` above (a real
  // `requestId` to correlate by, since `getManagedSources`'s handler is
  // modelled directly on `getArticlesFeed`'s own async-handler-with-no-
  // serialization race), NOT `filterSourcesResolversRef`'s fan-out-to-
  // everyone Set (`getSources`/`sourcesLoaded` has no requestId at all).
  const managedSourcesResolversRef = useRef<
    Map<
      string,
      {
        resolve: (data: { sources: ManagedSource[]; total: number; offset: number }) => void;
        reject: (error: Error) => void;
      }
    >
  >(new Map());

  const rejectPendingManagedSourcesRequests = (reason: string) => {
    managedSourcesResolversRef.current.forEach(({ reject }) => reject(new Error(reason)));
    managedSourcesResolversRef.current.clear();
  };

  // COM-640: `addSource`'s own correlation state for the SUBMISSION phase
  // only (before `sourceAdded` confirms the row exists) - `pendingAddSourceRef`
  // stays a single slot, not a Map/Set, even after the whole-branch review's
  // Critical 2 + 3 fix wave made the scan-tracking collection below a set.
  // This is a deliberate, narrower claim than "at most one scan is ever in
  // flight" (which is now false - see `scanState` below): AddSourceDialog
  // disables its own submit/cancel/close controls for the ENTIRE submission
  // round trip (`submitting`, AddSourceDialog.tsx) and only re-enables (by
  // closing) once `sourceAdded` arrives - so a SECOND `addSource` call can
  // only ever begin after the FIRST has already been confirmed (or refused).
  // Concurrency only ever exists between an already-confirmed scan (past
  // this pending slot entirely, now tracked in `scanState`) and a freshly
  // submitted one - never between two calls both still waiting on their
  // OWN first reply. That is what keeps this slot safe to correlate an
  // unattributable in-band `error` against (see the `error` listener below).
  //
  // Fix round (review, Critical 1): carries the submitted `url` alongside
  // the resolvers - the `sourceAdded` listener below only resolves THIS
  // pending call when a `sourceAdded` for the SAME url arrives, rather than
  // any `sourceAdded` at all. Without this, source discovery's own
  // `sourceAdded` (`backend/src/index.ts`'s `handleAutoGeneratedSource`,
  // a completely different url) firing while a manual add is pending would
  // wrongly resolve/correlate to it.
  const pendingAddSourceRef = useRef<{ url: string; resolve: () => void; reject: (error: Error) => void } | null>(
    null
  );

  // Fix wave (whole-branch review, Critical 2 + 3): was a single tagged
  // union (`idle | scanning | watching`) - now a SET of concurrent scans,
  // keyed by url (see addSourceScan.ts's own doc comment for the full
  // reasoning and the two bugs the single-scan model produced). `EMPTY_SCAN_STATE`
  // replaces the old `IDLE_SCAN_STATE` as the reducer's initial value.
  const [scanState, dispatchScan] = useReducer(addSourceScanReducer, EMPTY_SCAN_STATE);
  // Fix round (review, Critical 1): the `sourcesLoaded`/`error` listeners
  // below are registered once, in the mount-only `useEffect` (empty
  // dependency array), so they close over whatever `scanState` was at
  // MOUNT time unless given a way to read the current value - a ref kept in
  // sync on every render (same pattern `SourcesFilter.tsx` already uses for
  // `fetchFilterSourcesRef`), not a state read, since nothing here needs to
  // re-render off it.
  const scanStateRef = useRef<ScanState>(scanState);
  scanStateRef.current = scanState;
  // Fix round 2: lets the project-switch effect below tell an actual SWITCH
  // apart from the initial `null -> id` selection on load - see that
  // effect's own comment.
  const previousSelectedProjectIdRef = useRef<string | null>(null);
  const [sourceScanCompletedCount, setSourceScanCompletedCount] = useState(0);

  // Fix wave (Critical 3): was a SINGLE mutable timer backing the whole
  // submission -> completion safety net for the one scan the old model could
  // track. Now a Map keyed by url - the SAME key `scanState` itself uses
  // (see addSourceScan.ts's `ScanState` doc comment for why url, not source
  // id, is the right key) - so arming/clearing one scan's timer NEVER
  // touches a DIFFERENT concurrent scan's own timer. This is the direct fix
  // for Critical 3's reproduction: A confirmed (its completion-phase timer
  // armed) -> user submits B, a DIFFERENT url (arming B's submission-phase
  // timer used to call the SAME shared `clearScanTimeout`, wiping A's) -> A
  // completes (clearing B's net) -> B's `sourceAdded` arrives with nothing
  // left to resolve its promise, stranding `AddSourceDialog` open forever. A
  // per-url Map makes that cross-clearing structurally impossible for two
  // DIFFERENT urls.
  //
  // Fix wave (re-review, blocker): this map alone does NOT cover a
  // resubmission of the SAME url a confirmed scan is already running
  // under - arming a fresh submission-phase timer under that same key would
  // still clear the already-running scan's own completion-phase entry here.
  // That gap is closed by giving the submission phase an entirely separate
  // single slot (`pendingAddSourceTimerRef`, below `armScanTimer`) rather
  // than trying to make this Map's keying scheme handle both phases at
  // once - see that ref's own comment for the full reasoning. This Map is
  // now used for the COMPLETION phase only (armed on `sourceAdded`, see
  // that listener below).
  const scanTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // `useCallback` (empty deps): these all only ever touch ref `.current`s
  // and `dispatchScan` (a `useReducer` dispatch, guaranteed stable by React)
  // - genuinely identity-stable across renders - so wrapping them lets the
  // mount-only `useEffect` below reference them without
  // `react-hooks/exhaustive-deps` requiring them in its (deliberately empty)
  // dependency array, the same way `dispatchScan` itself never needs to be.
  const clearScanTimer = useCallback((url: string) => {
    const id = scanTimersRef.current.get(url);
    if (id !== undefined) {
      clearTimeout(id);
      scanTimersRef.current.delete(url);
    }
  }, []);

  const clearAllScanTimers = useCallback(() => {
    scanTimersRef.current.forEach((id) => clearTimeout(id));
    scanTimersRef.current.clear();
  }, []);

  const armScanTimer = useCallback(
    (url: string, ms: number, onTimeout: () => void) => {
      clearScanTimer(url);
      const id = setTimeout(() => {
        scanTimersRef.current.delete(url);
        onTimeout();
      }, ms);
      scanTimersRef.current.set(url, id);
    },
    [clearScanTimer]
  );

  // Fix wave (re-review, blocker): the SUBMISSION-phase safety net used to
  // be armed via `armScanTimer(url, ADD_SOURCE_TIMEOUT_MS, ...)` above -
  // sharing `scanTimersRef`'s per-url key with the COMPLETION-phase timer
  // armed on `sourceAdded` below. That is correct for one scan's own
  // submission timer being superseded by its OWN completion timer
  // (sequential phases of the SAME scan) - but the same key collision also
  // fires when a SECOND `addSource` call is submitted for a url that
  // already has a CONFIRMED, still-running scan: a same-url resubmission,
  // which AddSourceDialog's own closing-on-`sourceAdded` behaviour makes
  // reachable through the real UI (see `addSourceScan.ts`'s `ScanState` doc
  // comment for the full reproduction). Arming the resubmission's 30s
  // timer under the same url key silently wiped the FIRST scan's 12-minute
  // completion net before the backend's duplicate-URL refusal even arrived.
  //
  // Fixed by giving the submission phase its own single timer slot,
  // structurally unable to collide with `scanTimersRef`'s per-url map. A
  // single slot (not another per-url map) is correct, not just simpler:
  // `pendingAddSourceRef` below is ITSELF a single slot (see its own
  // comment for why a second `addSource` call can only ever begin once the
  // first has already settled) - there is only ever ONE submission-phase
  // timer alive at any moment, regardless of how many CONFIRMED scans are
  // concurrently running. `sourceAdded` (below) clears this explicitly the
  // moment it consumes the pending call, rather than relying on a same-key
  // overwrite to do it implicitly.
  const pendingAddSourceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingAddSourceTimer = useCallback(() => {
    if (pendingAddSourceTimerRef.current !== null) {
      clearTimeout(pendingAddSourceTimerRef.current);
      pendingAddSourceTimerRef.current = null;
    }
  }, []);

  const armPendingAddSourceTimer = useCallback(
    (ms: number, onTimeout: () => void) => {
      clearPendingAddSourceTimer();
      pendingAddSourceTimerRef.current = setTimeout(() => {
        pendingAddSourceTimerRef.current = null;
        onTimeout();
      }, ms);
    },
    [clearPendingAddSourceTimer]
  );

  // Unwinds every piece of `addSource`'s in-flight state at once - shared by
  // the 'connect'/'disconnect' handlers below (a stale pending add across a
  // reconnect can never be answered by a matching event again, same
  // reasoning as `rejectPendingArticlesFeedRequests`/
  // `rejectPendingFilterSourcesRequests`) and the project-switch effect
  // further below, so a dropped connection or a project switch mid-scan
  // doesn't strand the indicator any more than a refused add would.
  //
  // Fix wave (Critical 2 + 3): abandons EVERY tracked scan, not just the one
  // pre-confirmation pending call - `scanState` can now hold several
  // concurrent scans, and a disconnect/project-switch means nothing arriving
  // on a new connection (or after the user has moved to a different
  // project) can be trusted as ANY of their completions, not only the
  // oldest one.
  const rejectPendingAddSource = useCallback(
    (reason: string) => {
      const pending = pendingAddSourceRef.current;
      if (pending) {
        pendingAddSourceRef.current = null;
        clearPendingAddSourceTimer();
        pending.reject(new Error(reason));
      }
      if (Object.keys(scanStateRef.current).length > 0) {
        clearAllScanTimers();
        dispatchScan({ type: 'abandonAll' });
      }
    },
    [clearPendingAddSourceTimer, clearAllScanTimers]
  );

  // COM-647 Task 3: was this provider's OWN `toast` state + a raw MUI
  // `Snackbar`/`Alert` render, folded into the shared `ToastContext` host -
  // see that file's own doc comment for why (unifies the visual language,
  // and both were already anchored to the same top-right corner as
  // AllArticlesPage's own former inline toast, risking a visual stack if
  // both fired together). `ToastProvider` is mounted as an ANCESTOR of
  // `SourcesProvider` (App.tsx) precisely so this call is valid here.
  const { showToast } = useToast();

  // COM-647 Task 3: correlation state for the Sources Settings table's four
  // mutations (`updateSource`, `setSourceMonitored`, `deleteSources`,
  // `addSources`). Modelled on `addSource`'s own pattern above rather than a
  // second scheme (see `pendingAddSourceRef`'s comment): each mutation's
  // SUCCESS reply (`sourceUpdated` / `sourceMonitoredUpdated` /
  // `sourcesDeleted` / `sourcesAdded`) carries enough of its own payload to
  // correlate unambiguously, but the backend's plain in-band `error` (a
  // refusal or failure) carries no requestId and no source id at all - the
  // same documented limitation `addSource`'s own `error` handling lives
  // with (see the 'error' listener below).
  //
  // `updateSource`/`setSourceMonitored` correlate their SUCCESS reply by
  // sourceId (a Map, not a single slot): unlike `addSource`, which
  // `AddSourceDialog` gates one call at a time behind a single form, the
  // Settings table can plausibly have two DIFFERENT rows' Toggle switches (or
  // one row's edit and another's toggle) submitted back to back, and each
  // must settle independently rather than the second call silently
  // stranding the first. A resubmission for the SAME sourceId while one is
  // already pending DOES still overwrite that key's entry (same accepted
  // gap `pendingAddSourceRef` has for same-url resubmission before its own
  // fix - not revisited here since the Settings table UI is expected to
  // disable a row's own controls while its own request is in flight, the
  // same assumption AddSourceDialog's `submitting` already rests on).
  const pendingUpdateSourceResolversRef = useRef<
    Map<string, { resolve: (source: ManagedSource) => void; reject: (error: Error) => void }>
  >(new Map());
  const pendingSetSourceMonitoredResolversRef = useRef<
    Map<string, { resolve: (source: ManagedSource) => void; reject: (error: Error) => void }>
  >(new Map());

  // `deleteSources`/`addSources` (batch) are single-slot, not keyed maps:
  // each is driven by its own blocking UI (a confirm dialog / an add modal)
  // that the Settings page is expected to keep open - disabling further
  // submissions of the SAME kind - until it settles, the same "only one call
  // can be pre-confirmation pending at a time" assumption `pendingAddSourceRef`
  // documents for `addSource` itself. A second call while one is already
  // pending is refused outright (see `deleteSources`/`addSources` below)
  // rather than silently overwriting and stranding the first.
  const pendingDeleteSourcesRef = useRef<{
    resolve: (result: { deletedCount: number; sourceIds: string[] }) => void;
    reject: (error: Error) => void;
  } | null>(null);
  const pendingAddSourcesRef = useRef<{
    resolve: (sources: ManagedSource[]) => void;
    reject: (error: Error) => void;
  } | null>(null);

  // Rejects every currently pending one of the four mutations above - used
  // both on disconnect/reconnect (same reasoning as `rejectPendingAddSource`:
  // nothing pending across a dropped connection can be trusted to be
  // answered by a matching reply again) and, deliberately, on an
  // UNATTRIBUTABLE in-band `error` (see the listener below): this provider
  // has no way to know WHICH pending mutation such an error belongs to, so -
  // exactly like `rejectPendingAddSource`'s own "abandon EVERY tracked scan"
  // choice for the identical ambiguity - it rejects all of them rather than
  // guessing one. Over-firing here can spuriously fail a healthy sibling
  // mutation; under-firing would instead strand a genuinely failed one until
  // its own safety-net timeout. The same tradeoff, made the same way.
  const rejectPendingSourceSettingsMutations = useCallback((reason: string) => {
    pendingUpdateSourceResolversRef.current.forEach(({ reject }) => reject(new Error(reason)));
    pendingUpdateSourceResolversRef.current.clear();
    pendingSetSourceMonitoredResolversRef.current.forEach(({ reject }) => reject(new Error(reason)));
    pendingSetSourceMonitoredResolversRef.current.clear();
    if (pendingDeleteSourcesRef.current) {
      const pending = pendingDeleteSourcesRef.current;
      pendingDeleteSourcesRef.current = null;
      pending.reject(new Error(reason));
    }
    if (pendingAddSourcesRef.current) {
      const pending = pendingAddSourcesRef.current;
      pendingAddSourcesRef.current = null;
      pending.reject(new Error(reason));
    }
  }, []);

  // Initialize WebSocket connection
  useEffect(() => {
    const newSocket = createSocket();
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('Connected to WebSocket server');
      setIsConnected(true);
      setError(null);
      // Review round 2: a (re)connect means every socket-level listener the
      // PREVIOUS connection had is gone - any `articlesFeedLoaded` reply
      // still in flight when the drop happened will never arrive on this
      // new connection. Reject rather than leave dangling, so a caller
      // whose request crossed a reconnect settles (as a rejection the page,
      // a later task, treats as "retry") instead of hanging forever.
      rejectPendingArticlesFeedRequests('Socket reconnected before a reply arrived');
      // COM-639 fix round 1: same reasoning, for `fetchFilterSources` - a
      // `sourcesLoaded` still in flight when the drop happened will never
      // arrive on this new connection either.
      rejectPendingFilterSourcesRequests('Socket reconnected before a reply arrived');
      // COM-640: same reasoning, for `addSource` - see
      // `rejectPendingAddSource`'s own comment above.
      rejectPendingAddSource('Socket reconnected before a reply arrived');
      // COM-647 Task 3: same reasoning, for `fetchManagedSources` and the
      // Sources Settings table's four mutations.
      rejectPendingManagedSourcesRequests('Socket reconnected before a reply arrived');
      rejectPendingSourceSettingsMutations('Socket reconnected before a reply arrived');
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from WebSocket server');
      setIsConnected(false);
      // Same reasoning as 'connect' above: nothing pending across this drop
      // can ever be answered by an id-matching reply again.
      rejectPendingArticlesFeedRequests('Socket disconnected before a reply arrived');
      rejectPendingFilterSourcesRequests('Socket disconnected before a reply arrived');
      rejectPendingAddSource('Socket disconnected before a reply arrived');
      rejectPendingManagedSourcesRequests('Socket disconnected before a reply arrived');
      rejectPendingSourceSettingsMutations('Socket disconnected before a reply arrived');
    });

    newSocket.on('connect_error', (error: any) => {
      console.error('WebSocket connection error:', error);
      console.error('Error details:', error.message, error.description);
      setError(`Failed to connect to server: ${error.message}`);
      setIsConnected(false);
    });

    // Handle error events
    newSocket.on('error', (errorData: { message: string }) => {
      console.error('Server error:', errorData);
      // Fix round (COM-647 Task 3, finding 3): the pre-fold `Snackbar` this
      // replaced always auto-hid after 3s (`autoHideDuration={3000}`);
      // `showToast` with no `autoHideMs` persists until dismissed or
      // replaced (`ToastContext`'s own contract), so omitting it here
      // silently turned every background socket error into a
      // stays-forever toast. Restored explicitly rather than changing the
      // policy - see the report for whether persisting is actually the
      // better call for errors.
      showToast(errorData.message, 'error', { autoHideMs: 3000 });
      setError(errorData.message);
      setLoading(false);

      // COM-640: `addSource`'s refusal path (backend/src/index.ts's
      // `addSource` handler emits a plain `error`, never `sourceAdded`, for
      // a duplicate URL or the 100-source cap - it returns before creating
      // anything). No requestId to correlate this specifically to the
      // pending add (see `addSource`'s own doc comment on the interface
      // above) - the error is unambiguous here BECAUSE `pendingAddSourceRef`
      // stays a single slot even under concurrency (see that ref's own
      // comment for why a second call can never begin while this one is
      // still pre-confirmation).
      const pendingAdd = pendingAddSourceRef.current;
      if (pendingAdd) {
        pendingAddSourceRef.current = null;
        clearPendingAddSourceTimer();
        pendingAdd.reject(new Error(errorData.message));
        return;
      }

      // Fix round (review, Critical 2): `pendingAddSourceRef` is already
      // null past `sourceAdded` - the promise settled there - but a scan it
      // started can still be being watched (waiting on its own terminal
      // status). `backend/src/index.ts`'s `addSource` handler can emit a
      // plain in-band `error` AFTER `sourceAdded` too (e.g.
      // `sendAllSourcesWithFullArticles` throwing once
      // `processContentAsync` has already finished) WITHOUT dropping the
      // connection - previously this branch didn't exist at all, so that
      // error was silently swallowed and "Scanning 1 source" stayed up
      // until a manual reload (only a full disconnect unwound it).
      //
      // Fix wave (Critical 2 + 3, concurrency): with a SET of scans now
      // trackable, this in-band error carries no way to tell WHICH
      // confirmed/watching scan it belongs to (no requestId, no source id -
      // `backend/src/index.ts`'s catch block only ever emits a plain
      // message). No new socket event was introduced to add one (out of
      // this fix wave's constraints), so this abandons EVERY currently
      // tracked scan rather than guessing - the same accepted imprecision
      // the single-scan model already had (there, "abandon the one scan"
      // and "abandon everything" were identical by construction). This can
      // over-fire when several scans are genuinely healthy and only one hit
      // this rare failure path, but under-firing would silently strand a
      // real failure until the 12-minute safety net instead - the worse of
      // the two outcomes this feature exists to avoid.
      if (Object.keys(scanStateRef.current).length > 0) {
        clearAllScanTimers();
        dispatchScan({ type: 'abandonAll' });
      }

      // COM-647 Task 3: same unattributable-`error` gap as `addSource`'s own
      // documented limitation just above, extended to the Sources Settings
      // table's four mutations - see `rejectPendingSourceSettingsMutations`'s
      // own comment for the full reasoning. Only reached when this error
      // wasn't already claimed by `pendingAdd` above (that branch returns).
      rejectPendingSourceSettingsMutations(errorData.message);
    });

    // COM-640: `sourceAdded` = "your scan started" (backend/src/index.ts's
    // `addSource` handler emits this the instant the row exists, before
    // awaiting `processContentAsync`). Resolves the pending `addSource`
    // promise and records this scan's own source id (`confirmed`) so a
    // later `sourcesLoaded` can recognise THIS source reaching a terminal
    // status as "the scan finished" (`completedScanUrls`, addSourceScan.ts) -
    // not just any `sourcesLoaded` arriving.
    //
    // Fix round (review, Critical 1): only resolves when the payload's `url`
    // matches what THIS pending call submitted. `sourceAdded` is also
    // emitted by source discovery's `handleAutoGeneratedSource`
    // (backend/src/index.ts) for a completely different url - without this
    // check, a discovery run landing a source while a manual add is pending
    // would wrongly resolve the manual add's promise (and arm completion
    // tracking) against the WRONG source. A `sourceAdded` that doesn't match
    // - wrong url, or nothing pending at all (e.g. a stray after this client
    // already gave up via the timeout net) - is a silent no-op; the pending
    // call keeps waiting for its own.
    newSocket.on('sourceAdded', (source: { id: string; url: string }) => {
      const pending = pendingAddSourceRef.current;
      if (!pending || pending.url !== source.url) {
        return;
      }
      pendingAddSourceRef.current = null;
      // Fix wave (re-review, blocker): explicitly clears the SUBMISSION-
      // phase timer rather than relying on `armScanTimer` below to clear it
      // as a same-key side effect (that sharing was exactly the bug - see
      // `pendingAddSourceTimerRef`'s own comment). This phase's timer is
      // now a dedicated single slot that can only ever hold THIS pending
      // call's own timer, so clearing it here can never touch a different,
      // already-confirmed scan's completion-phase entry in `scanTimersRef`.
      clearPendingAddSourceTimer();
      dispatchScan({ type: 'confirmed', url: source.url, sourceId: source.id });
      // Re-arm the safety net for the completion phase - keyed by THIS
      // scan's own url in the per-scan timer map, so it can never be cleared
      // by a concurrent scan's own arm/clear (Critical 3's fix - see
      // `scanTimersRef`'s doc comment). See `ADD_SOURCE_SCAN_TIMEOUT_MS`'s
      // own comment for why this needs a materially longer budget than the
      // submission round trip above.
      //
      // Fix round 2: dispatches `timedOut`, not `failed` - this only ever
      // fires while `sourceId` is already known (armed here, right after
      // `confirmed`), so the scan genuinely running server-side must not be
      // abandoned just because the spinner has waited long enough. `timedOut`
      // (addSourceScan.ts) stops the VISIBLE scan but keeps `sourceId` under
      // observation, so a later `sourcesLoaded` reaching a terminal status
      // still bumps `sourceScanCompletedCount` and triggers the catch-up
      // refetch / filter-cache invalidation - late, but not lost.
      armScanTimer(source.url, ADD_SOURCE_SCAN_TIMEOUT_MS, () => {
        dispatchScan({ type: 'timedOut', url: source.url });
      });
      pending.resolve();
    });

    // Handle projects loaded event
    newSocket.on('projects', (data: Project[]) => {
      setProjects(data);
    });

    // Article summary loaded
    newSocket.on('articleSummaryLoaded', (data: { url: string; summary: ArticleSummary | null }) => {
      // this event will be handled inline via a Promise resolver; we keep the listener to avoid unknown event
    });

    // All Articles feed loaded (COM-638). One persistent listener for the
    // whole socket's lifetime - see `articlesFeedResolversRef` above for why
    // this can't be a per-call `socket.on`/`off` pair, and why matching is by
    // `requestId` rather than offset. Exactly one pending resolver is
    // consumed per reply - the one whose id matches, regardless of arrival
    // order.
    newSocket.on('articlesFeedLoaded', (data: ArticlesFeedLoadedEvent) => {
      if (!data.requestId) {
        return;
      }

      const pending = articlesFeedResolversRef.current.get(data.requestId);
      if (!pending) {
        return;
      }

      articlesFeedResolversRef.current.delete(data.requestId);

      // `error: true` (fix wave): the handler's OWN catch block hit a
      // failure and echoed this back instead of `articles`/`total` (see
      // backend/src/index.ts) - reject rather than resolve, with a plain
      // (non-`FeedSocketDisconnectError`) Error so the page's toast/error
      // path handles it, not the disconnect banner.
      if (data.error) {
        pending.reject(new Error('The server failed to load the articles feed'));
        return;
      }

      pending.resolve({
        articles: data.articles ?? [],
        total: data.total ?? 0,
        offset: data.offset,
        // COM-640: absent only on the `error: true` shape, which returns
        // above before reaching here - always a real value on this path.
        lastUpdatedAt: data.lastUpdatedAt ?? null,
      });
    });

    // COM-639 fix round 1: `sourcesLoaded` reply for `fetchFilterSources`.
    // One persistent listener, same reason as `articlesFeedLoaded` above -
    // but resolved differently, because `getSources` has no requestId to
    // correlate a reply to a specific call.
    //
    // Verified EVERY `sourcesLoaded` emit site in the backend (grepped
    // `backend/src/index.ts`, the only file that emits it - two KINDS of
    // site, both checked; re-verified in the COM-647 fix round below, since
    // that ticket added four more call sites of kind 1):
    //   1. `sendAllSourcesWithFullArticles` re-fetches from the DB and emits
    //      the project's CURRENT full source list every time. Called from:
    //      `getSources`, `addSource`, `deleteSource`, `refreshSourceContent`,
    //      `handleAutoGeneratedSource` (source discovery's own per-attempt
    //      completion), and, since COM-647, `updateSource`,
    //      `setSourceMonitored`, `deleteSources`, and `addSources` (this last
    //      one calls it once PER created source, right after that source's
    //      own `processContentAsync` resolves - see its own comment in
    //      `backend/src/index.ts` for why a batch needs a per-source signal
    //      rather than one call after the whole batch - but it is still the
    //      same truthful "full current list" re-fetch each time, just fired
    //      more than once for one client call).
    //   2. `discardData`'s handler emits a bare `[]` immediately after
    //      `sourcesService.discardAllData(projectId)` deletes every source
    //      for that project - also the truthful current state at that
    //      instant (the project genuinely now has zero sources), not a
    //      partial or stale payload.
    // No site of either kind ever emits anything other than "the project's
    // sources right now" - so it is correct, not just convenient, to resolve
    // EVERY pending `fetchFilterSources` call with whichever `sourcesLoaded`
    // arrives while it's waiting: each of them asked "what are the
    // project's sources right now", and any emit site truthfully answers
    // that question, including the discard's `[]`. If a future emit site
    // ever answers something partial/stale instead, this fan-out resolve
    // becomes wrong and needs revisiting.
    //
    // Fix wave (2026-08-18, Artur): the filter predicate used to be
    // `source.status === 'completed'` ("active" sources per the original
    // AC), then briefly "has at least one article" (exactly the feed's own
    // universe, since `DatabaseService#getArticlesFeed` has no
    // source-status predicate at all). Refined once more to a three-case
    // rule - see `fetchFilterSources`'s interface doc comment above and
    // `shouldOfferSourceInFilter` (frontend/src/lib/filterSources.ts) for
    // the exact predicate:
    //   - no error, no articles   -> include (a freshly added/pending
    //     source the user must be able to filter by even before it has
    //     produced anything yet)
    //   - articles AND an error   -> include (its articles are already in
    //     the feed, so the user needs a checkbox to select them by)
    //   - error AND zero articles -> exclude (pure noise: it contributes
    //     nothing to the feed and is broken)
    // The raw payload here carries each source's `articles` array
    // (`getAllSourcesWithFullArticles` in dbService.ts / the `sourcesLoaded`
    // fixture in mockSocket.ts); `Source` itself (types.ts) has no
    // `articles` field, so the payload is typed locally as
    // `SourcesLoadedPayload` rather than widening the shared type for a
    // field only this listener reads. The resolved value strips `articles`
    // back off (via the rest-spread below) so callers keep getting the same
    // `Source[]` shape they always have.
    newSocket.on('sourcesLoaded', (sources: SourcesLoadedPayload) => {
      const offeredSources = (sources || [])
        .filter(shouldOfferSourceInFilter)
        .map(({ articles, ...source }) => source);
      filterSourcesResolversRef.current.forEach(({ resolve }) => resolve(offeredSources));
      filterSourcesResolversRef.current.clear();

      // Fix round (review, Critical 1): completion used to be "ANY
      // `sourcesLoaded` arrived while `awaitingOwnScanCompletionRef` was
      // set" - a bare boolean with no correlation to WHICH request armed
      // it. `sourcesLoaded` is shared with `fetchFilterSources`'s own
      // `getSources` round trip (COM-639) and source discovery's per-source
      // `sendAllSourcesWithFullArticles` - a user opening the Sources
      // dropdown mid-scan fires exactly that `getSources`, whose reply was
      // being misread as "my scan finished": the indicator dropped early, a
      // catch-up refetch fired before the article existed, and the REAL
      // completion afterwards was silently ignored (nothing was "awaiting"
      // it anymore) - the article never appeared.
      //
      // Fixed by deciding completion off each tracked scan's OWN source id
      // reaching a terminal status in the raw (pre-filter) payload -
      // `completedScanUrls` (addSourceScan.ts), verified against
      // `sourcesService.ts`: `addSource` creates a row `in_progress`,
      // `processContentAsync` always ends by writing exactly `completed` or
      // `error`. A dropdown-triggered `getSources` mid-scan reports a scan's
      // source still `in_progress` - correctly NOT a match, so the real
      // completion later is still recognised. `sources` (not
      // `offeredSources`) on purpose: the filter predicate above can
      // legitimately drop the very source a scan is watching (e.g. an
      // `error`-terminal source with no articles), and completion must not
      // depend on whether the dropdown would currently offer it.
      //
      // Fix wave (Critical 2 + 3, concurrency): `completedScanUrls` can
      // return more than one url when several concurrent scans happen to
      // reach a terminal status in the SAME `sourcesLoaded` reply - each is
      // finished independently (its own timer cleared, its own entry
      // removed from `scanState`) and the completion counter bumps once PER
      // scan, not once per reply, so `AllArticlesPage`'s catch-up refetch
      // and `SourcesFilter`'s cache invalidation see every completion, not
      // just the first.
      const finishedUrls = completedScanUrls(scanStateRef.current, sources || []);
      if (finishedUrls.length > 0) {
        finishedUrls.forEach((url) => {
          clearScanTimer(url);
          dispatchScan({ type: 'finished', url });
        });
        setSourceScanCompletedCount((count) => count + finishedUrls.length);
      }
    });

    // COM-647 Task 3: `managedSourcesLoaded` reply for `fetchManagedSources` -
    // one persistent listener, same reason as `articlesFeedLoaded` above,
    // matched the same way (by `requestId`, not fan-out).
    newSocket.on(
      'managedSourcesLoaded',
      (data: { requestId: string | null; sources?: ManagedSource[]; total?: number; offset: number; error?: boolean }) => {
        if (!data.requestId) {
          return;
        }

        const pending = managedSourcesResolversRef.current.get(data.requestId);
        if (!pending) {
          return;
        }

        managedSourcesResolversRef.current.delete(data.requestId);

        if (data.error) {
          pending.reject(new Error('The server failed to load the managed sources'));
          return;
        }

        pending.resolve({
          sources: data.sources ?? [],
          total: data.total ?? 0,
          offset: data.offset,
        });
      }
    );

    // COM-647 Task 3: `updateSource`'s own actionable reply - the updated
    // row. Matched by the row's own id, same technique `sourceAdded` above
    // uses (matched by url) - a stray `sourceUpdated` with no matching
    // pending call (e.g. after this client's own timeout already gave up)
    // is a silent no-op.
    newSocket.on('sourceUpdated', (source: ManagedSource) => {
      const pending = pendingUpdateSourceResolversRef.current.get(source.id);
      if (!pending) {
        return;
      }
      pendingUpdateSourceResolversRef.current.delete(source.id);
      pending.resolve(source);
    });

    // COM-647 Task 3: the Active/Inactive toggle's own actionable reply -
    // same matching technique as `sourceUpdated` above.
    newSocket.on('sourceMonitoredUpdated', (source: ManagedSource) => {
      const pending = pendingSetSourceMonitoredResolversRef.current.get(source.id);
      if (!pending) {
        return;
      }
      pendingSetSourceMonitoredResolversRef.current.delete(source.id);
      pending.resolve(source);
    });

    // COM-647 Task 3: bulk delete's own actionable reply - a single slot
    // (see `pendingDeleteSourcesRef`'s own comment for why), so no id
    // matching is needed: whichever `sourcesDeleted` arrives while one is
    // pending IS the answer to it.
    newSocket.on('sourcesDeleted', (result: { deletedCount: number; sourceIds: string[] }) => {
      const pending = pendingDeleteSourcesRef.current;
      if (!pending) {
        return;
      }
      pendingDeleteSourcesRef.current = null;
      pending.resolve(result);
    });

    // COM-647 Task 3: batch add's own actionable reply - the created rows,
    // fired once at the end of the whole batch (backend/src/index.ts's
    // `addSources` handler). Same single-slot matching as `sourcesDeleted`
    // above. Deliberately NOT wired into `scanState`/`scanningSourceCount` -
    // unlike `addSource`'s own per-entry `sourceAdded` (which this handler
    // ALSO emits per created source, ignored here the same way it's ignored
    // by everything except a matching `pendingAddSourceRef`), integrating a
    // batch add with the single-add scanning indicator was left out of this
    // task's scope; see this method's own comment below.
    newSocket.on('sourcesAdded', (sources: ManagedSource[]) => {
      const pending = pendingAddSourcesRef.current;
      if (!pending) {
        return;
      }
      pendingAddSourcesRef.current = null;
      pending.resolve(sources);
    });

    // Handle project data updated event
    newSocket.on('projectDataUpdated', (data: any) => {
      if (!data) {
        return
      }
      setProjects(prev => prev.map(p => p.id === data.id ? data : p));
    });

    // Handle auto-complete started event
    newSocket.on('autoCompleteStarted', () => {
      console.log('autoCompleteStarted received');
      setIsAutoCompleteInProgress(true);
    });

    // Handle auto-complete finished event
    newSocket.on('autoCompleteFinished', () => {
      console.log('autoCompleteFinished received');
      setIsAutoCompleteInProgress(false);
    });

    // Handle project users loaded event
    newSocket.on('projectUsersLoaded', (users: ProjectUser[]) => {
      console.log('Project users loaded:', users);
      setProjectUsers(users);
    });

    // Handle authentication-related events
    newSocket.on('authRequired', () => {
      console.log('Authentication required for this action');
      window.location.reload();
    });

    return () => {
      newSocket.close();
    };
    // `armScanTimer`/`clearScanTimer`/`clearAllScanTimers`/`rejectPendingAddSource`/
    // `clearPendingAddSourceTimer` are listed because this effect calls them
    // directly (inside the 'connect'/'disconnect'/'error'/'sourceAdded'/
    // 'sourcesLoaded' handlers above) - safe to include despite the
    // otherwise mount-only intent of this effect because all five are
    // `useCallback`-memoized with dependency arrays that only ever resolve
    // to other equally stable values (refs, `dispatchScan`), so their
    // identity never changes across renders and this effect still only ever
    // runs once.
    //
    // COM-647 Task 3: `rejectPendingSourceSettingsMutations` (`useCallback`,
    // `[]` deps - same stability argument) and `showToast` (`ToastContext`'s
    // own `useCallback`, stable across `ToastProvider` renders) join the list
    // for the same reason - both are called directly inside the 'connect'/
    // 'disconnect'/'error' handlers above.
  }, [
    armScanTimer,
    clearScanTimer,
    clearAllScanTimers,
    rejectPendingAddSource,
    clearPendingAddSourceTimer,
    rejectPendingSourceSettingsMutations,
    showToast,
  ]);

  // Load projects when socket connects and user is authenticated
  useEffect(() => {
    if (isConnected && socket && isAuthenticated) {
      console.log('Socket connected and user authenticated, loading projects...');
      loadProjects();
    }
  }, [isConnected, socket, isAuthenticated]);

  useEffect(() => {
    if (!socket || !user || !projects.length || selectedProjectId) {
      return;
    }

    setProjectUsers(null)

    if (user.selectedProjectId) {
      const projectToSelect = projects.find(p => p.id === user.selectedProjectId) || null;
      if (projectToSelect) {
        setSelectedProjectId(projectToSelect.id);
        return
      }
    }

    setSelectedProjectId(projects[0].id);
    socket.emit('saveSelectedProject', { projectId: projects[0].id });
  }, [user, projects, selectedProjectId, socket]);

  // Load project data when project is selected
  useEffect(() => {
    if (selectedProjectId && isConnected && socket) {
      loadProjectUsers();
    }
  }, [selectedProjectId, isConnected, socket]);

  // Fix round 2 (COM-640 Task 2 follow-up, re-review): abandons every
  // in-flight add-source scan - visible or already `watching` past its
  // timeout, and however many are concurrently tracked (whole-branch review,
  // Critical 2 + 3) - on a PROJECT SWITCH, the same way `rejectPendingAddSource`
  // already does for a disconnect. `sourcesLoaded` carries no `projectId` (verified in
  // `backend/src/index.ts`/`dbService.ts` - every emit site just replies with
  // "this project's sources right now"), and source ids are opaque strings
  // with no cross-project namespacing guarantee either, so a scan started
  // against the OLD project has no way to tell a `sourcesLoaded` for the NEW
  // one apart from its own completion once the id it's matching on could, in
  // principle, coincide. Simpler and safer to just stop trusting it the
  // moment the user has visibly moved on - a stale completion for a project
  // the user isn't even looking at anymore has no catch-up refetch worth
  // running. `previousSelectedProjectIdRef` distinguishes an actual SWITCH
  // (skip the abandonment) from the initial `null -> id` selection on load,
  // which must not abort a scan that was just started.
  useEffect(() => {
    const previousProjectId = previousSelectedProjectIdRef.current;
    previousSelectedProjectIdRef.current = selectedProjectId;
    if (previousProjectId !== null && previousProjectId !== selectedProjectId) {
      rejectPendingAddSource('Project changed before the scan finished');
      // COM-647 Task 3: same reasoning, for `fetchManagedSources` and the
      // Sources Settings table's four mutations - a pending call against the
      // OLD project's data has nothing trustworthy to resolve into once the
      // user has switched away from it.
      rejectPendingManagedSourcesRequests('Project changed before a reply arrived');
      rejectPendingSourceSettingsMutations('Project changed before a reply arrived');
    }
  }, [selectedProjectId, rejectPendingAddSource, rejectPendingSourceSettingsMutations]);

  const addSource = (name: string, url: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!socket) {
        reject(new Error('Not connected to the server'));
        return;
      }

      setError(null);
      // Optimistic: dispatched the moment the call is made, before the
      // server has confirmed anything - see addSourceScan.ts's doc comment
      // for why 'started' has to fire here rather than on `sourceAdded`.
      // Carries `url`, which is also the key this scan is tracked under in
      // `scanState` for its WHOLE lifecycle - see addSourceScan.ts's
      // `ScanState` doc comment for why. A concurrent scan already in
      // `scanState` (from an earlier `addSource` call whose OWN dialog
      // instance already closed) is untouched: `started` only ever adds an
      // entry for THIS url.
      dispatchScan({ type: 'started', url });

      pendingAddSourceRef.current = {
        url,
        resolve: () => {
          resolve();
        },
        reject: (rejectionError) => {
          dispatchScan({ type: 'failed', url });
          reject(rejectionError);
        },
      };

      // Fix round (review, Critical 2): armed here rather than a bare
      // `setTimeout` local to this call, so a disconnect/project-switch
      // (`rejectPendingAddSource`) or `sourceAdded`/refusal arriving can all
      // clear the SAME tracked timer through one shared helper.
      //
      // Fix wave (re-review, blocker): this used to be `armScanTimer(url,
      // ...)` - the per-url completion-phase map - on the theory that the
      // timer "survives past `sourceAdded` into the completion phase" under
      // the same key. That theory is what broke on a same-url resubmission:
      // arming THIS call's 30s submission timer under a url that already
      // has a CONFIRMED scan silently cleared that scan's own 12-minute
      // completion timer first (see `pendingAddSourceTimerRef`'s own
      // comment for the full reproduction). The submission phase now uses
      // its own dedicated single-slot timer instead, which is handed off
      // explicitly (not via key reuse) once `sourceAdded` arms the real
      // completion-phase timer below - see that listener's own comment.
      armPendingAddSourceTimer(ADD_SOURCE_TIMEOUT_MS, () => {
        const pending = pendingAddSourceRef.current;
        if (pending && pending.url === url) {
          pendingAddSourceRef.current = null;
          pending.reject(new Error('Timed out waiting for a reply to add the source'));
        }
      });

      socket.emit('addSource', { name, url, projectId: selectedProjectId });
    });
  };

  // Restores the read/unread flip the old ContentArea drove via
  // `updateArticleStatus` (COM-638 fix - see the interface comment above).
  // Fire-and-forget, same guard as every other emitter here; no reply
  // listener is registered since the caller applies its own optimistic
  // local update.
  const markArticleRead = (sourceId: string, articleUrl: string) => {
    if (socket && selectedProjectId) {
      socket.emit('updateArticleStatus', { sourceId, articleUrl, isNew: false, projectId: selectedProjectId });
    }
  };

  const handleSetProjectData = (data: ClientData) => {
    if (socket && selectedProjectId) {
      socket.emit('setProjectData', {
        projectId: selectedProjectId,
        projectData: data
      });
    }
  };

  // The settings-page (COM-638) save path. Unlike `handleSetProjectData`,
  // this deliberately does not trigger auto-discovery — it only edits an
  // already-configured project.
  const handleUpdateProjectData = (data: ClientData) => {
    if (socket && selectedProjectId) {
      socket.emit('updateProjectData', {
        projectId: selectedProjectId,
        projectData: data
      });
    }
  };

  // Explicit source discovery (COM-637): unlike `handleSetProjectData`, this
  // is never a side effect of saving the project profile - the user has to
  // ask for it via its own control. Same guards, same selectedProjectId
  // handling as `handleUpdateProjectData`.
  const handleStartSourceDiscovery = () => {
    if (socket && selectedProjectId) {
      socket.emit('startSourceDiscovery', {
        projectId: selectedProjectId
      });
    }
  };

  const loadProjects = () => {
    if (socket) {
      socket.emit('loadProjects');
    } else {
      console.log('Cannot load projects: socket or user not available');
    }
  };

  const discardData = () => {
    if (socket && isConnected) {
      socket.emit('discardData', { projectId: selectedProjectId });
      window.location.reload();
    }
  }

  const selectProject = (projectId: string) => {
    setSelectedProjectId(projectId);
    
    if (socket && projectId && projects.some(project => project.id === projectId)) {
      console.log('💾 Manually saving selected project:', projectId);
      socket.emit('saveSelectedProject', { projectId: projectId });
    }
  };

  // === PROJECT USERS MANAGEMENT ===
  
  const loadProjectUsers = () => {
    // The backend refuses every user-management event for embedded sessions
    // (COM-636, a security boundary — see regulations-auth-access). Asking
    // anyway only earns an error toast on a surface that already hides user
    // management, so don't ask.
    if (isEmbedded()) {
      return;
    }

    if (socket && selectedProjectId) {
      socket.emit('getProjectUsers', { projectId: selectedProjectId });
    }
  };

  const addUserToProject = (email: string) => {
    if (socket && selectedProjectId && email.trim()) {
      socket.emit('addUserToProject', { 
        projectId: selectedProjectId,
        email: email.trim() 
      });
    }
  };

  const removeUserFromProject = (userId: string) => {
    if (socket && selectedProjectId) {
      socket.emit('removeUserFromProject', { 
        projectId: selectedProjectId,
        targetUserId: userId 
      });
    }
  };

  const updateUserProjectRole = (userId: string, role: 'VIEWER' | 'OWNER') => {
    if (socket && selectedProjectId) {
      socket.emit('updateUserProjectRole', { 
        projectId: selectedProjectId,
        targetUserId: userId, 
        role 
      });
    }
  };

  const updateProjectGPTModel = (gptModel: string) => {
    if (socket && selectedProjectId) {
      socket.emit('updateProjectGPTModel', {
        projectId: selectedProjectId,
        gptModel: gptModel
      });
    }
  };

  const getArticleSummary = (url: string): Promise<ArticleSummary | null> => {
    return new Promise((resolve) => {
      if (!socket || !selectedProjectId) {
        resolve(null);
        return;
      }

      const handler = (data: { url: string; summary: ArticleSummary | null }) => {
        if (data.url === url) {
          socket.off('articleSummaryLoaded', handler as any);
          resolve(data.summary || null);
        }
      };

      socket.on('articleSummaryLoaded', handler as any);
      socket.emit('getArticleSummary', { projectId: selectedProjectId, url });
    });
  };

  // All Articles feed (COM-638). Unlike `getArticleSummary` above, this
  // can't use a per-call `socket.on`/`off` pair matched by a fixed key, and
  // (review round 2) not even a FIFO queue matched by offset - offset alone
  // can't tell two same-offset requests' replies apart once they arrive out
  // of send order (`getArticlesFeed`'s handler is async with no
  // serialization between concurrent calls on one socket). Each call gets
  // its own opaque `requestId`; the resolver is keyed on that id in
  // `articlesFeedResolversRef`, and the one persistent `articlesFeedLoaded`
  // listener registered above resolves exactly the matching entry,
  // regardless of arrival order.
  const fetchArticlesFeed = (params: {
    limit?: number;
    offset?: number;
    sortBy?: 'date' | 'title';
    sortDir?: 'asc' | 'desc';
    search?: string;
    sourceIds?: string[];
    dateFrom?: string;
    dateTo?: string;
  }): Promise<ArticlesFeedPage> => {
    const requestId = generateArticlesFeedRequestId();

    return new Promise((resolve, reject) => {
      if (!socket || !selectedProjectId) {
        resolve({ articles: [], total: 0, offset: normalizeFeedRequest(params).offset, lastUpdatedAt: null });
        return;
      }

      // Safety net (fix wave): guarantees this promise settles even if
      // NEITHER a matching `articlesFeedLoaded` reply NOR a disconnect/
      // reconnect ever arrives for it - the case a `projectAccessCheck`
      // access-guard rejection falls into, since that path never reaches
      // the `getArticlesFeed` handler and so can't echo a requestId back at
      // all. A plain Error (not `FeedSocketDisconnectError`) - a timeout is
      // not a disconnect, and must not get the "Connection lost" treatment.
      const timeoutId = setTimeout(() => {
        if (articlesFeedResolversRef.current.delete(requestId)) {
          reject(new Error('Timed out waiting for a reply to the articles feed request'));
        }
      }, ARTICLES_FEED_TIMEOUT_MS);

      articlesFeedResolversRef.current.set(requestId, {
        resolve: (data) => {
          clearTimeout(timeoutId);
          resolve(data);
        },
        reject: (error) => {
          clearTimeout(timeoutId);
          reject(error);
        },
      });

      socket.emit('getArticlesFeed', { projectId: selectedProjectId, ...params, requestId });
    });
  };

  // COM-639: lean, on-demand fetch for the All Articles page's Sources
  // filter dropdown - scoped to a single call rather than persistent
  // context state (the design spec calls for "a lean fetch ... scoped to
  // the filter dropdown, not global context state"; the dropdown loads its
  // list lazily on first open).
  //
  // Fix round 1 (review): an earlier version used a one-shot `on`/`off`
  // pair with NO discriminant at all, on the theory that any `sourcesLoaded`
  // reply is an equally valid answer. That reasoning is right (see the
  // persistent listener's comment above for the verified list of emit
  // sites), but the implementation was wrong in two ways review caught:
  //   1. It could resolve from a `sourcesLoaded` that had nothing to do
  //      with this call (e.g. a concurrent `discardData`), stranding the
  //      REAL reply this call actually wanted with no listener left to
  //      catch it.
  //   2. It never settled at all if no `sourcesLoaded` ever arrived (the
  //      same `requireProjectAccess` access-guard-rejection gap
  //      `ARTICLES_FEED_TIMEOUT_MS` exists for on `fetchArticlesFeed` -
  //      `getSources` sits behind the same guard) - the dropdown would spin
  //      forever with a stale listener still attached, able to fire on any
  //      later unrelated `sourcesLoaded`.
  // Fixed by reusing `fetchArticlesFeed`'s own machinery instead of a third
  // pattern: this call's resolver joins `filterSourcesResolversRef`'s
  // pending set (settled by the persistent listener above, or rejected on
  // disconnect/reconnect), guarded by the SAME `ARTICLES_FEED_TIMEOUT_MS`
  // safety-net timeout.
  const fetchFilterSources = (): Promise<Source[]> => {
    return new Promise((resolve, reject) => {
      if (!socket || !selectedProjectId) {
        resolve([]);
        return;
      }

      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const pendingEntry = {
        resolve: (data: Source[]) => {
          if (timeoutId) clearTimeout(timeoutId);
          resolve(data);
        },
        reject: (error: Error) => {
          if (timeoutId) clearTimeout(timeoutId);
          reject(error);
        },
      };

      timeoutId = setTimeout(() => {
        if (filterSourcesResolversRef.current.delete(pendingEntry)) {
          reject(new Error('Timed out waiting for a reply to the sources request'));
        }
      }, ARTICLES_FEED_TIMEOUT_MS);

      filterSourcesResolversRef.current.add(pendingEntry);
      socket.emit('getSources', { projectId: selectedProjectId });
    });
  };

  // COM-647 Task 3: the Sources Settings table's own paged/filtered/sorted
  // fetch - `getManagedSources`/`managedSourcesLoaded` (backend/src/index.ts's
  // read handler, added this task; the lean `DatabaseService#getManagedSources`
  // query itself was added Task 2). Modelled on `fetchArticlesFeed` above,
  // not `fetchFilterSources`: a real `requestId` correlates each call to its
  // own reply (see `managedSourcesResolversRef`'s own comment for why this
  // table's own race is the same shape as the feed's, not the filter
  // dropdown's fan-out-to-everyone one).
  const fetchManagedSources = (params: {
    limit?: number;
    offset?: number;
    sortBy?: 'name' | 'createdAt';
    sortDir?: 'asc' | 'desc';
    search?: string;
    monitored?: boolean;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ sources: ManagedSource[]; total: number; offset: number }> => {
    const requestId = generateManagedSourcesRequestId();

    return new Promise((resolve, reject) => {
      if (!socket || !selectedProjectId) {
        resolve({ sources: [], total: 0, offset: params.offset ?? 0 });
        return;
      }

      // Safety net (same reasoning as `fetchArticlesFeed`'s own): guarantees
      // this promise settles even if a `requireProjectAccess` access-guard
      // rejection never reaches the `getManagedSources` handler at all, so
      // it can never echo a matching `requestId` back.
      const timeoutId = setTimeout(() => {
        if (managedSourcesResolversRef.current.delete(requestId)) {
          reject(new Error('Timed out waiting for a reply to the managed sources request'));
        }
      }, MANAGED_SOURCES_TIMEOUT_MS);

      managedSourcesResolversRef.current.set(requestId, {
        resolve: (data) => {
          clearTimeout(timeoutId);
          resolve(data);
        },
        reject: (error) => {
          clearTimeout(timeoutId);
          reject(error);
        },
      });

      socket.emit('getManagedSources', { projectId: selectedProjectId, ...params, requestId });
    });
  };

  // COM-647 Task 3: edit a source's name/url (Sources Settings table).
  // Resolves with the updated row on `sourceUpdated`; rejects on the shared
  // 'error' listener's per-sourceId correlation (see
  // `pendingUpdateSourceResolversRef`'s own comment above for the exact
  // correlation contract and its accepted gap on an unattributable `error`).
  const updateSource = (sourceId: string, data: { name?: string; url: string }): Promise<ManagedSource> => {
    return new Promise((resolve, reject) => {
      if (!socket || !selectedProjectId) {
        reject(new Error('Not connected to the server'));
        return;
      }

      const timeoutId = setTimeout(() => {
        if (pendingUpdateSourceResolversRef.current.delete(sourceId)) {
          reject(new Error('Timed out waiting for a reply to update the source'));
        }
      }, SOURCE_SETTINGS_MUTATION_TIMEOUT_MS);

      pendingUpdateSourceResolversRef.current.set(sourceId, {
        resolve: (source) => {
          clearTimeout(timeoutId);
          resolve(source);
        },
        reject: (error) => {
          clearTimeout(timeoutId);
          reject(error);
        },
      });

      socket.emit('updateSource', { projectId: selectedProjectId, sourceId, name: data.name, url: data.url });
    });
  };

  // COM-647 Task 3: the Active/Inactive toggle. Flips `isMonitored` only -
  // same correlation contract as `updateSource` above.
  const setSourceMonitored = (sourceId: string, isMonitored: boolean): Promise<ManagedSource> => {
    return new Promise((resolve, reject) => {
      if (!socket || !selectedProjectId) {
        reject(new Error('Not connected to the server'));
        return;
      }

      const timeoutId = setTimeout(() => {
        if (pendingSetSourceMonitoredResolversRef.current.delete(sourceId)) {
          reject(new Error('Timed out waiting for a reply to update source monitoring'));
        }
      }, SOURCE_SETTINGS_MUTATION_TIMEOUT_MS);

      pendingSetSourceMonitoredResolversRef.current.set(sourceId, {
        resolve: (source) => {
          clearTimeout(timeoutId);
          resolve(source);
        },
        reject: (error) => {
          clearTimeout(timeoutId);
          reject(error);
        },
      });

      socket.emit('setSourceMonitored', { projectId: selectedProjectId, sourceId, isMonitored });
    });
  };

  // COM-647 Task 3: bulk delete - several ids in ONE round trip (see
  // `pendingDeleteSourcesRef`'s own comment for why this is a single slot,
  // not a per-id Map). Refuses a second call outright while one is already
  // pending, rather than silently overwriting and stranding the first -
  // the Settings page's own confirm dialog is expected to stay open (and
  // its own controls disabled) for the whole round trip anyway.
  const deleteSources = (sourceIds: string[]): Promise<{ deletedCount: number; sourceIds: string[] }> => {
    return new Promise((resolve, reject) => {
      if (!socket || !selectedProjectId) {
        reject(new Error('Not connected to the server'));
        return;
      }

      if (pendingDeleteSourcesRef.current) {
        reject(new Error('A delete is already in progress'));
        return;
      }

      const timeoutId = setTimeout(() => {
        const pending = pendingDeleteSourcesRef.current;
        if (pending) {
          pending.reject(new Error('Timed out waiting for a reply to delete the sources'));
        }
      }, SOURCE_SETTINGS_MUTATION_TIMEOUT_MS);

      pendingDeleteSourcesRef.current = {
        resolve: (result) => {
          clearTimeout(timeoutId);
          pendingDeleteSourcesRef.current = null;
          resolve(result);
        },
        reject: (error) => {
          clearTimeout(timeoutId);
          pendingDeleteSourcesRef.current = null;
          reject(error);
        },
      };

      socket.emit('deleteSources', { projectId: selectedProjectId, sourceIds });
    });
  };

  // COM-647 Task 3: batch add for the "+ Add another source" modal rows.
  // Resolves with the created rows on `sourcesAdded`, fired once at the end
  // of the WHOLE batch (backend/src/index.ts's `addSources` handler runs
  // each entry's `processContentAsync` sequentially before moving to the
  // next) - so, unlike `SOURCE_SETTINGS_MUTATION_TIMEOUT_MS`'s flat 30s,
  // this call's own safety net scales with the batch size: each entry can
  // legitimately take as long as a single `addSource`'s own scan
  // (`ADD_SOURCE_SCAN_TIMEOUT_MS`, sized for a first scan's sequential
  // catalog-check/relevance-filter/summary chain), and they never run in
  // parallel. Deliberately NOT integrated with `scanState`/
  // `scanningSourceCount` (the All Articles page's "Scanning N sources"
  // indicator) - that would mean deciding how a Settings-page batch add
  // should surface on a DIFFERENT page's indicator, which is a UI-design
  // question for the page that actually renders the modal (a later task),
  // not this context-layer method.
  const addSources = (entries: { name?: string; url: string }[]): Promise<ManagedSource[]> => {
    return new Promise((resolve, reject) => {
      if (!socket || !selectedProjectId) {
        reject(new Error('Not connected to the server'));
        return;
      }

      if (pendingAddSourcesRef.current) {
        reject(new Error('A batch add is already in progress'));
        return;
      }

      const batchTimeoutMs = Math.max(entries.length, 1) * ADD_SOURCE_SCAN_TIMEOUT_MS;

      const timeoutId = setTimeout(() => {
        const pending = pendingAddSourcesRef.current;
        if (pending) {
          pending.reject(new Error('Timed out waiting for a reply to add the sources'));
        }
      }, batchTimeoutMs);

      pendingAddSourcesRef.current = {
        resolve: (sources) => {
          clearTimeout(timeoutId);
          pendingAddSourcesRef.current = null;
          resolve(sources);
        },
        reject: (error) => {
          clearTimeout(timeoutId);
          pendingAddSourcesRef.current = null;
          reject(error);
        },
      };

      socket.emit('addSources', { projectId: selectedProjectId, entries });
    });
  };

  // Permission check function
  const canManageProject = () => {
    if (!user || !selectedProjectId) return false;
    
    // Admins can manage any project
    if (user.isAdmin) return true;
    
    // Project owners can manage their projects
    const currentProject = projects.find(p => p.id === selectedProjectId);
    return currentProject?.role === 'OWNER';
  };

  const value: SourcesContextType = {
    loading,
    error,
    addSource,
    scanningSourceCount: scanningCount(scanState),
    sourceScanCompletedCount,
    markArticleRead,
    isConnected,
    setProjectData: handleSetProjectData,
    updateProjectData: handleUpdateProjectData,
    startSourceDiscovery: handleStartSourceDiscovery,
    isAutoCompleteInProgress,
    discardData,
    // New project-related values
    projects,
    selectedProject: projects.find((project) => project.id === selectedProjectId) || null,
    selectProject,
    // Project users management
    projectUsers,
    loadProjectUsers,
    addUserToProject,
    removeUserFromProject,
    updateUserProjectRole,
    // GPT Model
    updateProjectGPTModel,
    // Permission checks
    canManageProject,
    // Article summary
    getArticleSummary,
    // All Articles feed
    fetchArticlesFeed,
    fetchFilterSources,
    // Sources Settings table
    fetchManagedSources,
    updateSource,
    setSourceMonitored,
    deleteSources,
    addSources,
  };

  return (
    <SourcesContext.Provider value={value}>
      {children}
    </SourcesContext.Provider>
  );
};

export const useSources = (): SourcesContextType => {
  const context = useContext(SourcesContext);
  if (context === undefined) {
    throw new Error('useSources must be used within a SourcesProvider');
  }
  return context;
}; 